import { useRef, useState, useEffect, useCallback } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import useAuth from 'hooks/useAuth';
import { useGetNotifications, markAllNotificationsRead, NOTIFICATION_TYPES, getTimeAgo } from 'api/notifications';
import { listNotifications, markRead, markAllRead } from 'services/api/backendNotifications';
import { approveRequest, rejectRequest } from 'services/api/approvals';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import CloudSyncOutlined from '@ant-design/icons/CloudSyncOutlined';
import CloudUploadOutlined from '@ant-design/icons/CloudUploadOutlined';
import UserSwitchOutlined from '@ant-design/icons/UserSwitchOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';

// sx styles
const avatarSX = { width: 36, height: 36, fontSize: '1rem' };
const actionSX = { mt: '6px', ml: 1, top: 'auto', right: 'auto', alignSelf: 'flex-start', transform: 'none' };

const getNotificationStyle = (notification) => {
  const type = notification.type;
  const color = notification.color || 'info';
  switch (type) {
    case NOTIFICATION_TYPES?.SYNC_SUCCESS:
      return { icon: <CloudUploadOutlined />, avatarColor: 'success.main', avatarBg: 'success.lighter' };
    case NOTIFICATION_TYPES?.SYNC_FAILED:
      return { icon: <ExclamationCircleOutlined />, avatarColor: 'error.main', avatarBg: 'error.lighter' };
    case NOTIFICATION_TYPES?.SYNC_PARTIAL:
      return { icon: <WarningOutlined />, avatarColor: 'warning.main', avatarBg: 'warning.lighter' };
    case NOTIFICATION_TYPES?.SYNC_NO_DATA:
      return { icon: <CloudSyncOutlined />, avatarColor: 'info.main', avatarBg: 'info.lighter' };
    case 'approval_request':
      return { icon: <UserSwitchOutlined />, avatarColor: 'warning.main', avatarBg: 'warning.lighter' };
    case 'approval_approved':
      return { icon: <CheckCircleOutlined />, avatarColor: 'success.main', avatarBg: 'success.lighter' };
    case 'approval_rejected':
      return { icon: <ExclamationCircleOutlined />, avatarColor: 'error.main', avatarBg: 'error.lighter' };
    default:
      return {
        icon:
          color === 'success' ? (
            <CheckCircleOutlined />
          ) : color === 'error' ? (
            <ExclamationCircleOutlined />
          ) : color === 'warning' ? (
            <WarningOutlined />
          ) : (
            <InfoCircleOutlined />
          ),
        avatarColor: `${color}.main`,
        avatarBg: `${color}.lighter`
      };
  }
};

// ==============================|| BACKEND NOTIFICATION ROW ||============================== //

function ApprovalNotificationRow({ notif, onAction }) {
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [actionError, setActionError] = useState(null);

  const approvalId = notif.action_data?.approval_request_id;

  const handleApprove = async () => {
    if (!approvalId) return;
    setBusy(true);
    setActionError(null);
    try {
      await approveRequest(approvalId);
      await markRead(notif.id);
      setDone(true);
      onAction?.();
    } catch (e) {
      setActionError(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!approvalId || !reason.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      await rejectRequest(approvalId, reason.trim());
      await markRead(notif.id);
      setDone(true);
      onAction?.();
    } catch (e) {
      setActionError(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const style = getNotificationStyle(notif);

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: notif.is_read ? 'transparent' : 'action.hover' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ px: 2, py: 1.5 }}>
        <Avatar sx={{ width: 36, height: 36, color: style.avatarColor, bgcolor: style.avatarBg, flexShrink: 0 }}>{style.icon}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {notif.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {notif.message}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {notif.created_at ? getTimeAgo(notif.created_at) : ''}
          </Typography>
          {notif.action_type === 'approve_supervisor' && !done && !notif.is_read && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {!rejectMode ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckOutlined />}
                    disabled={busy}
                    onClick={handleApprove}
                    sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<CloseOutlined />}
                    disabled={busy}
                    onClick={() => setRejectMode(true)}
                    sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
                  >
                    Reject
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  <TextField
                    size="small"
                    placeholder="Rejection reason..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    sx={{ fontSize: '0.75rem' }}
                    inputProps={{ style: { fontSize: '0.75rem' } }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      disabled={busy || !reason.trim()}
                      onClick={handleReject}
                      sx={{ fontSize: '0.7rem', py: 0.3 }}
                    >
                      Confirm Reject
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setRejectMode(false);
                        setReason('');
                      }}
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              )}
              {actionError && (
                <Alert severity="error" sx={{ py: 0, fontSize: '0.7rem' }}>
                  {actionError}
                </Alert>
              )}
            </Stack>
          )}
          {(done || (notif.action_type === 'approve_supervisor' && notif.is_read)) && (
            <Alert severity="success" sx={{ mt: 0.5, py: 0, fontSize: '0.7rem' }}>
              Already handled.
            </Alert>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const { notifications } = useGetNotifications();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  // Backend notifications (for clinicalhead and supervisor roles)
  const isClinicalHead = user?.role === 'clinicalhead';
  const useBackendNotifs = isClinicalHead || user?.role === 'supervisor';
  const [backendNotifs, setBackendNotifs] = useState([]);
  // Timestamp (ms) of the last time the panel was opened, persisted across reloads.
  // Used to clear the badge without marking items read (read state gates approval actions).
  const [seenAt, setSeenAt] = useState(() => {
    const v = typeof window !== 'undefined' ? window.localStorage.getItem('notif_seen_at') : null;
    return v ? new Date(v).getTime() : 0;
  });

  const loadBackendNotifs = useCallback(async () => {
    if (!useBackendNotifs) return;
    try {
      const notifRes = await listNotifications();
      setBackendNotifs(Array.isArray(notifRes) ? notifRes : []);
    } catch {
      /* ignore */
    }
  }, [useBackendNotifs]);

  useEffect(() => {
    loadBackendNotifs();
  }, [loadBackendNotifs]);

  // Refresh backend notifs when panel opens
  useEffect(() => {
    if (open && useBackendNotifs) {
      loadBackendNotifs();
    }
  }, [open, useBackendNotifs, loadBackendNotifs]);

  // Poll for new notifications so the badge/list stay fresh without a reload
  useEffect(() => {
    if (!useBackendNotifs) return undefined;
    const id = setInterval(loadBackendNotifs, 60000);
    return () => clearInterval(id);
  }, [useBackendNotifs, loadBackendNotifs]);

  // Opening the panel marks everything currently visible as "seen" -> clears the badge.
  // We intentionally do NOT mark items read here, so approval action buttons remain.
  useEffect(() => {
    if (!open) return;
    const now = Date.now();
    setSeenAt(now);
    try {
      window.localStorage.setItem('notif_seen_at', new Date(now).toISOString());
    } catch {
      /* ignore storage errors */
    }
  }, [open]);

  // Badge counts only unread items newer than the last time the panel was opened
  const backendUnseen = backendNotifs.filter((n) => !n.is_read && new Date(n.created_at).getTime() > seenAt).length;
  const inMemoryUnseen = notifications.filter((n) => !n.read && new Date(n.timestamp).getTime() > seenAt).length;
  const totalUnread = inMemoryUnseen + backendUnseen;

  // Whether any unread items exist at all (controls the "mark all as read" action)
  const hasUnread = notifications.some((n) => !n.read) || backendNotifs.some((n) => !n.is_read);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleMarkAllRead = () => {
    markAllNotificationsRead();
    if (useBackendNotifs) {
      markAllRead()
        .then(loadBackendNotifs)
        .catch(() => {});
    }
  };

  // "Mark all as read" action, shared between the desktop popover and mobile drawer
  const markAllReadButton = hasUnread ? (
    <Tooltip title="Mark all as read">
      <IconButton color="success" size="small" onClick={handleMarkAllRead}>
        <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
      </IconButton>
    </Tooltip>
  ) : null;

  // The notification list body, shared between the desktop popover and mobile drawer
  const notificationList = (
    <>
      {/* Backend notifications for clinical head and supervisor */}
      {useBackendNotifs && backendNotifs.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.75, display: 'block', fontWeight: 600 }}>
            {isClinicalHead ? 'APPROVAL REQUESTS' : 'NOTIFICATIONS'}
          </Typography>
          {backendNotifs.map((notif) => (
            <ApprovalNotificationRow key={notif.id} notif={notif} onAction={loadBackendNotifs} />
          ))}
          {notifications.length > 0 && <Divider sx={{ my: 0.5 }} />}
        </>
      )}

      {/* In-memory notifications */}
      <List
        component="nav"
        sx={{
          p: 0,
          '& .MuiListItemButton-root': {
            py: 0.5,
            px: 2,
            '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
            '& .MuiAvatar-root': avatarSX,
            '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
          }
        }}
      >
        {notifications.length === 0 && (!isClinicalHead || backendNotifs.length === 0) ? (
          <ListItem>
            <ListItemText
              primary={
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                  No notifications
                </Typography>
              }
            />
          </ListItem>
        ) : (
          notifications.map((notification) => {
            const style = getNotificationStyle(notification);
            return (
              <ListItem
                key={notification.id}
                component={ListItemButton}
                divider
                selected={!notification.read}
                secondaryAction={
                  <Typography variant="caption" noWrap>
                    {getTimeAgo(notification.timestamp)}
                  </Typography>
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ color: style.avatarColor, bgcolor: style.avatarBg }}>{style.icon}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={<Typography variant="h6">{notification.title}</Typography>} secondary={notification.message} />
              </ListItem>
            );
          })
        )}
      </List>
    </>
  );

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={totalUnread} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      {downSM ? (
        // Mobile: full-screen drawer notification center (mirrors Theme Customization panel)
        <Drawer anchor="right" open={open} onClose={handleClose} sx={{ zIndex: 2001 }} slotProps={{ paper: { sx: { width: '100%' } } }}>
          <MainCard
            title="Alerts & Reminders"
            content={false}
            sx={{
              border: 'none',
              borderRadius: 0,
              height: '100vh',
              '& .MuiCardHeader-root': { bgcolor: 'primary.main', '& .MuiTypography-root': { color: 'background.paper', fontSize: '1rem' } }
            }}
            secondary={
              <Stack direction="row" spacing={0.5} alignItems="center">
                {markAllReadButton}
                <IconButton size="small" onClick={handleClose} aria-label="close notifications" sx={{ color: 'background.paper' }}>
                  <CloseOutlined style={{ fontSize: '1.15rem' }} />
                </IconButton>
              </Stack>
            }
          >
            <Box sx={{ height: 'calc(100vh - 56px)', overflowY: 'auto', overflowX: 'hidden' }}>{notificationList}</Box>
          </MainCard>
        </Drawer>
      ) : (
        <Popper
          placement="bottom-end"
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
        >
          {({ TransitionProps }) => (
            <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
              <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: 440 })}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MainCard title="Alerts & Reminders" elevation={0} border={false} content={false} secondary={markAllReadButton}>
                    <Box sx={{ maxHeight: 340, overflowY: 'auto', overflowX: 'hidden' }}>{notificationList}</Box>
                  </MainCard>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </Popper>
      )}
    </Box>
  );
}
